import CommandHandler from 'diagram-js/lib/command/CommandHandler';
import {Base, Shape} from 'diagram-js/lib/model';
import {RulesetUtils} from '@ispm-client/modules/bpmn-modeler/utils/ruleset-utils';
import {NodeCreationResult} from '@ispm-client/modules/bpmn-modeler/models/node-creation-result';
import {
  guideBoxPropertyExtensionType,
  nodeDetailsField,
  nodesDetailsExtensionType
} from '@ispm-shared/modules/bpmn/diagram-element-terms';
import {PathwaysModeling} from '@ispm-client/modules/bpmn-modeler/bpmn/commands/pathways-modeling';
import {ModelingUtil} from '@ispm/shared/bpmn-io/modeling-util';
import {BpmnExtensionHelper} from '@ispm/shared/bpmn-io/bpmn-extension-helper';
import {NodeType} from '@ispm/shared/bpmn-io/node-type';
import EventBus from 'diagram-js/lib/core/EventBus';
import {EventType} from '@ispm/shared/bpmn-io/bpmn-js';
import * as _ from 'lodash';
import {Rule} from '@ispm-client/modules/query-builder/models/query-builder-config';
import {CreationResult} from "@ispm-client/modules/bpmn-modeler/models/creation-result";
import {TreatmentNodeUtils} from "@ispm/shared/bpmn-io/treatment-node-utils";

/**
 * Created By Elia Rohana on 1/9/19
 */

export class NodeDataCreationHandler implements CommandHandler {

  constructor(private modeling,
              private pathwaysModeling: PathwaysModeling,
              private moddle,
              private bpmnExtensionHelper: BpmnExtensionHelper,
              private eventBus: EventBus) {
  }

  execute(context): Base[] {
    return [context.element];
  }

  postExecute(context): any {
    const element = context.element;
    const nodeCreationResult = context.nodeCreationResult;
    const nodeType = context.nodeType;

    if (nodeType === NodeType.treatment) {
      this.setTreatmentNodeProperties(nodeCreationResult, element);
    } else {
      this.modeling.updateProperties(element, {name: nodeCreationResult.name});

      if (nodeCreationResult.guideBox) {
        this.appendGuidedBoxData(element, nodeCreationResult);
      }

      if (nodeCreationResult.ruleset && nodeType !== NodeType.head) {
        this.appendNodeConditionData(element, nodeCreationResult);
      }
    }

    this.eventBus.fire(EventType.NODE_UPDATED, {node: element, nodeCreationResult, nodeType});
    return element;
  }

  private appendNodeConditionData(shape: Shape, nodeCreationResult: NodeCreationResult) {
    let condition = RulesetUtils.extractConditionExpression(nodeCreationResult.id, nodeCreationResult.ruleset, true);
    const ruleset = nodeCreationResult.ruleset;

    const rules = RulesetUtils.extractRules(nodeCreationResult.id, ruleset);
    const nodeDetails = ModelingUtil.getNodeDetails(shape) || this.moddle.create(nodesDetailsExtensionType);
    const gateway = ModelingUtil.getNodeIncomingGateway(shape);

    if (rules.length > 0 && gateway) {
      const isComplicatedRule = RulesetUtils.hasComplicatedRule(ruleset);
      nodeDetails.complicated = isComplicatedRule;
      const fields = [];
      // Add manual select condition, like this:
      // ({gatewayId}_manual_selection == null && condition) || {gatewayId}_manual_selection == '{nodeId}'
      // This handles the following cases:
      // 1. We don't have the data for the condition, and the user needs to manually select the next node
      // 2. Conditions of multiple children are met - so the BPMN cannot know which node to go to => manual selection required
      const manualSelectNodeCondition: Rule = RulesetUtils.addManualSelectCondition(nodeCreationResult, shape.id, gateway.id);
      fields.push(this.moddle.create(nodeDetailsField, {
        key: manualSelectNodeCondition.fullName,
        value: RulesetUtils.parseValue(manualSelectNodeCondition),
        type: manualSelectNodeCondition.valueType
      }));
      // Use the original condition ONLY if no manual selection was made
      // If manual selection was made, use it
      const noManualSelectNodeCondition: string = RulesetUtils.parseRuleToIsEMpty(manualSelectNodeCondition);
      condition = RulesetUtils.wrapConditionWithManualSelectLogic(condition, manualSelectNodeCondition, noManualSelectNodeCondition);
      if (!isComplicatedRule) {
        for (const rule of rules) {
          const key = rule.key;
          const value = RulesetUtils.parseValue(rule);
          if (key !== "" && value !== "") {
            fields.push(this.moddle.create(nodeDetailsField,
              {key: key, value: value, type: rule.valueType}));
          }

        }
      }

      nodeDetails.fields = fields;
    }
    this.bpmnExtensionHelper.addOrReplaceExtension(shape, nodeDetails);
    if (!_.isEmpty(condition) && ModelingUtil.getNodeType(shape) !== NodeType.neighborhood_head) {
      ModelingUtil.getNodeIncomingConnections(shape)
        .forEach(connection => this.pathwaysModeling.appendConnectionExpression(connection, condition));
    }
  }

  private appendGuidedBoxData(shape: Shape, nodeCreationResult: NodeCreationResult) {
    let guideBox = ModelingUtil.getGuideBox(shape);
    // remove old guided box data
    if (guideBox) {
      this.bpmnExtensionHelper.removeExtension(shape, guideBoxPropertyExtensionType);
      this.eventBus.fire(EventType.GUIDEBOX_DATA_REMOVED, {
        node: shape
      });
    }
    // add the new guided box data from the nodeCreationResult
    if (!this.isObjectEmpty(nodeCreationResult.guideBox)) {
      guideBox = guideBox || this.moddle.create(guideBoxPropertyExtensionType);
      guideBox.automatically = !!nodeCreationResult.guideBox.automatically;
      this.bpmnExtensionHelper.addOrReplaceExtension(shape, guideBox);
    }
  }

  private isObjectEmpty(value) {
    if (_.isEmpty(value)) {
      return true;
    }
    for (const prop in value) {
      if (prop === "guidingText") {
        if (!_.isEmpty(value[prop].replace(/<\/?[^>]+(>|$)/g, ""))) {
          return false;
        }
      } else {
        if (!_.isEmpty(value[prop])) {
          return false;
        }
      }
    }
    return true;
  }

  private setTreatmentNodeProperties(data: CreationResult, node: Shape) {
    const treatmentPlans = _.get(data, 'treatmentPlans', []);
    const clinicalTrial = _.get(data, 'clinicalTrial', undefined);
    const treatmentPlanNodeName = TreatmentNodeUtils.createName(treatmentPlans, clinicalTrial);

    this.addNodeDetail(node, 'additionalNote', data.additionalNote);
    const primaryTreatmentPlanArr = treatmentPlans.filter(treatmentPlan => treatmentPlan.primary);
    this.addNodeDetail(node, 'hasPrimaryTreatment', !!primaryTreatmentPlanArr && primaryTreatmentPlanArr.length > 0);
    this.modeling.updateProperties(node, {name: treatmentPlanNodeName});
  }

  private addNodeDetail(node: Shape, propertyName: string, propertyValue) {
    const nodeDetails = ModelingUtil.getNodeDetails(node) || this.moddle.create(nodesDetailsExtensionType);
    nodeDetails[propertyName] = propertyValue;
    this.bpmnExtensionHelper.addOrReplaceExtension(node, nodeDetails);
  }
}
